import React, { useState, useRef, useEffect } from 'react';

export interface DropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface ScorecardDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  placeholder?: string;
  loadingText?: string;
  disabled?: boolean;
  className?: string;
  variant?: 'default' | 'header' | 'inline';
  showArrow?: boolean;
  loading?: boolean;
}

const ScorecardDropdown: React.FC<ScorecardDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  loadingText = 'Loading...',
  disabled = false,
  className = '',
  variant = 'default',
  showArrow = true,
  loading = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const getSelectedLabel = () => {
    if (loading) return loadingText;
    const selectedOption = options.find(option => option.value === value);
    return selectedOption ? selectedOption.label : placeholder;
  };

  const getVariantClasses = () => {
    switch (variant) {
      case 'header':
        return 'block w-full text-2xl font-bold text-gray-900 bg-transparent border-none appearance-none focus:outline-none focus:ring-0 cursor-pointer';
      case 'inline':
        return 'inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';
      default:
        return 'inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500';
    }
  };

  const getDropdownClasses = () => {
    switch (variant) {
      case 'header':
        return 'absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50';
      case 'inline':
        return 'absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50';
      default:
        return 'absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50';
    }
  };

  if (variant === 'header') {
    return (
      <div className="relative" ref={dropdownRef}>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || loading}
          className={getVariantClasses()}
        >
          {loading ? (
            <option value="">{loadingText}</option>
          ) : options.length > 0 ? (
            options.map(option => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))
          ) : (
            <option value="">No options found</option>
          )}
        </select>
        {showArrow && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        disabled={disabled || loading}
        className={`${getVariantClasses()} ${disabled || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {variant === 'default' && (
          <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        )}
        {getSelectedLabel()}
        {showArrow && (
          <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>

      {isOpen && (
        <div className={getDropdownClasses()}>
          <div className="py-1">
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleOptionSelect(option.value)}
                disabled={option.disabled}
                className={`block w-full text-left px-4 py-2 text-sm transition-colors duration-200 ${
                  option.value === value
                    ? 'bg-indigo-100 text-indigo-700 font-medium'
                    : option.disabled
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {option.label}
              </button>
            ))}
            {options.length === 0 && (
              <div className="px-4 py-2 text-sm text-gray-500">
                No options available
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScorecardDropdown;
